import React, { useEffect, useMemo } from 'react';
import {
  Modal,
  ModalVariants,
  Typography,
  Button,
  Loader,
} from '@hid-galaxy-ui/galaxy-react';
import Iconography, {
  IconographySizes,
} from '@hid-galaxy-ui/galaxy-react/components/Iconography';
import { IconographyThemes } from '@hid-galaxy-ui/galaxy-react/components/Iconography/iconEnums';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import { LoaderSizes } from '@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums';
import { useTranslation } from 'react-i18next';

import { IRequirementItem } from '../../constants/imageRules';
import { useProjectImageRules } from '../../hooks/useProjectImageRules';
import {
  getFriendlyColorName,
  normalizeHexColor,
} from '../../utils/photoValidation';
import { mapImageRulesToRequirements } from '../../utils/imageRequirementsMapper';
import { getRequirementIcon } from './requirementIcons';
import { usePhotoRequirementsDialogStyles } from './photoRequirementsDialogStyles';

export interface IPhotoRequirementsDialogProps {
  open: boolean;
  projectId: string | null | undefined;
  tenantId: string | null | undefined;
  onClose: () => void;
}

interface IRequirementRowProps {
  requirement: IRequirementItem;
}

const RequirementRow: React.FC<IRequirementRowProps> = ({ requirement }) => {
  const classes = usePhotoRequirementsDialogStyles();
  const { t } = useTranslation();

  const normalizedColor = requirement.backgroundColor
    ? normalizeHexColor(requirement.backgroundColor)
    : null;
  const colorName = normalizedColor
    ? getFriendlyColorName(normalizedColor, t)
    : null;

  return (
    <div className={classes.requirementRow}>
      <Iconography
        icon="circleCheck"
        size={IconographySizes.Medium}
        theme={IconographyThemes.Success}
        className={classes.checkIcon}
      />
      <span className={classes.requirementIconWrap}>
        {getRequirementIcon(requirement.iconId, {
          className: classes.requirementIcon,
        })}
      </span>
      <div className={classes.requirementText}>
        <Typography
          variant={TypographyVariantEnum.Label}
          className={classes.requirementTitle}
        >
          {t(requirement.titleKey) as string}
        </Typography>
        <Typography
          variant={TypographyVariantEnum.TextHelper}
          className={classes.requirementDescription}
        >
          {t(requirement.descriptionKey, {
            ...(requirement.descriptionValues ?? {}),
          }) as string}
        </Typography>
        {normalizedColor && (
          <div className={classes.colorSwatchRow}>
            <span className={classes.colorSwatchLabel}>
              {t('PHOTO_VALIDATION_REQUIRED_BACKGROUND') as string}
            </span>
            <span
              className={classes.colorSwatch}
              style={{ backgroundColor: normalizedColor }}
              aria-hidden="true"
            />
            {colorName && (
              <span className={classes.colorSwatchName}>{colorName}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PhotoRequirementsDialog: React.FC<IPhotoRequirementsDialogProps> = ({
  open,
  projectId,
  tenantId,
  onClose,
}) => {
  const classes = usePhotoRequirementsDialogStyles();
  const { t } = useTranslation();
  const { data, loading, error, loadImageRules, retry } = useProjectImageRules(
    projectId,
    tenantId,
  );

  useEffect(() => {
    if (open) {
      loadImageRules();
    }
  }, [open, loadImageRules]);

  const requirements = useMemo(
    () => mapImageRulesToRequirements(data),
    [data],
  );

  if (!open) {
    return null;
  }

  return (
    <Modal
      size={ModalVariants.Medium}
      isDisplayCloseIcon
      onClose={onClose}
      className={classes.modal}
      isFocusTrapActive={false}
    >
      <div className={classes.root}>
        <div className={classes.header}>
          <span className={classes.headerIconWrap}>
            <Iconography
              icon="circleCamera"
              size={IconographySizes.Medium}
              theme={IconographyThemes.Info}
              className={classes.headerIcon}
            />
          </span>
          <div className={classes.headerText}>
            <Typography
              id="photo-requirements-title"
              variant={TypographyVariantEnum.H2}
              className={classes.title}
            >
              {t('PHOTO_REQUIREMENTS_TITLE') as string}
            </Typography>
            <Typography
              variant={TypographyVariantEnum.TextHelper}
              className={classes.subtitle}
            >
              {t('PHOTO_REQUIREMENTS_SUBTITLE') as string}
            </Typography>
          </div>
        </div>

        <div className={classes.content}>
          {loading && (
            <div className={classes.loadingWrap}>
              <Loader variant={LoaderSizes.medium} />
            </div>
          )}

          {!loading && error && (
            <div className={classes.errorWrap}>
              <Typography
                variant={TypographyVariantEnum.Label}
                className={classes.errorTitle}
              >
                {t('PHOTO_REQUIREMENTS_LOAD_ERROR') as string}
              </Typography>
              <Typography
                variant={TypographyVariantEnum.TextHelper}
                className={classes.errorSubtitle}
              >
                {t('PHOTO_REQUIREMENTS_LOAD_ERROR_RETRY') as string}
              </Typography>
              <Button
                variant="secondary"
                className={classes.retryButton}
                label={t('PHOTO_REQUIREMENTS_RETRY') as string}
                onClick={retry}
              />
            </div>
          )}

          {!loading && !error && (
            <div className={classes.requirementsList}>
              {requirements.map((requirement) => (
                <RequirementRow
                  key={requirement.id}
                  requirement={requirement}
                />
              ))}
            </div>
          )}
        </div>

        {!loading && !error && (
          <div className={classes.infoBox}>
            <Iconography
              icon="circleInfo"
              size={IconographySizes.Small}
              theme={IconographyThemes.Info}
              className={classes.infoIcon}
            />
            <Typography
              variant={TypographyVariantEnum.TextHelper}
              className={classes.infoText}
            >
              {t('PHOTO_REQUIREMENTS_INFO') as string}
            </Typography>
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <Button
          variant="primary"
          className={classes.okButton}
          label={t('PHOTO_REQUIREMENTS_GOT_IT') as string}
          onClick={onClose}
          disabled={loading}
        />
      </div>
    </Modal>
  );
};

export default PhotoRequirementsDialog;
